import { useEffect, useState } from "react";
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";

interface Props {
  open: boolean;
  callCount: number;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

// Confirmation for "re-run AI analysis on every call". Explains what changes
// (scores, leads, mentions, brief) and what doesn't (your decisions, notes).
const ReanalyzeModal: React.FC<Props> = ({ open, callCount, onClose, onConfirm }) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <div className="modal-box max-w-md p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <ArrowPathIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Re-run AI analysis?</h3>
            <p className="text-sm text-base-content/60 mt-0.5">
              Every call is read again{callCount ? ` (${callCount} call${callCount === 1 ? "" : "s"})` : ""}. Useful after you change the investigation focus or add context.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <ul className="mx-6 mb-5 rounded-xl border border-base-300 bg-base-200/50 divide-y divide-base-300 text-sm">
          <li className="px-4 py-2.5 flex gap-3">
            <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wider text-base-content/50 pt-0.5">Updated</span>
            <span className="text-base-content/80">Relevance scores, summaries, leads, mentions, and the brief.</span>
          </li>
          <li className="px-4 py-2.5 flex gap-3">
            <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wider text-base-content/50 pt-0.5">Kept</span>
            <span className="text-base-content/80">Confirmed and dismissed leads, your notes, speaker names, and chat history.</span>
          </li>
        </ul>
        <div className="px-6 pb-6 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={confirm} disabled={busy}>
            {busy ? "Starting…" : "Re-run analysis"}
          </Button>
        </div>
      </div>
      <button type="button" className="modal-backdrop" onClick={() => !busy && onClose()} aria-label="Close" />
    </dialog>
  );
};

export default ReanalyzeModal;
